/**
 * Common Validation Schemas
 *
 * Zod schemas for common data types
 */
import { z } from 'zod';
/**
 * Base entity schema
 */
export const baseEntitySchema = z.object({
    id: z.string().uuid(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().optional(),
    companyId: z.string().uuid(),
    createdBy: z.string().uuid().optional(),
    updatedBy: z.string().uuid().optional(),
});
/**
 * Pagination params schema
 */
export const paginationParamsSchema = z.object({
    page: z.number().int().min(1).default(1),
    limit: z.number().int().min(1).max(100).default(20),
    sortBy: z.string().optional(),
    sortOrder: z.enum(['asc', 'desc']).default('asc'),
});
/**
 * Address schema
 */
export const addressSchema = z.object({
    line1: z.string().min(1).max(255),
    line2: z.string().max(255).optional(),
    city: z.string().min(1).max(100),
    state: z.string().min(1).max(100),
    postalCode: z.string().min(1).max(20),
    country: z.string().length(2), // ISO 3166-1 alpha-2
    latitude: z.number().min(-90).max(90).optional(),
    longitude: z.number().min(-180).max(180).optional(),
});
/**
 * Contact info schema
 */
export const contactInfoSchema = z.object({
    name: z.string().min(1).max(255),
    email: z.string().email().optional(),
    phone: z.string().min(1).max(50).optional(),
    alternatePhone: z.string().min(1).max(50).optional(),
});
/**
 * Money schema
 */
export const moneySchema = z.object({
    amount: z.number().int().min(0),
    currency: z.string().length(3), // ISO 4217
});
/**
 * Dimensions schema
 */
export const dimensionsSchema = z.object({
    length: z.number().positive(),
    width: z.number().positive(),
    height: z.number().positive(),
    unit: z.enum(['cm', 'in', 'm', 'ft']),
});
/**
 * Weight schema
 */
export const weightSchema = z.object({
    value: z.number().positive(),
    unit: z.enum(['kg', 'lb', 'g', 'oz']),
});
/**
 * Attachment schema
 */
export const attachmentSchema = z.object({
    id: z.string().uuid(),
    filename: z.string().min(1).max(255),
    mimeType: z.string().min(1).max(100),
    size: z.number().int().positive(),
    url: z.string().url(),
    uploadedAt: z.date(),
    uploadedBy: z.string().uuid(),
});
/**
 * Note schema
 */
export const noteSchema = z.object({
    id: z.string().uuid(),
    content: z.string().min(1).max(10000),
    createdAt: z.date(),
    createdBy: z.string().uuid(),
    updatedAt: z.date().optional(),
    isInternal: z.boolean().default(false),
});
/**
 * Search params schema
 */
export const searchParamsSchema = z.object({
    query: z.string().optional(),
    fields: z.array(z.string()).optional(),
    filters: z.record(z.any()).optional(),
    dateRange: z
        .object({
        from: z.date(),
        to: z.date(),
        field: z.string(),
    })
        .optional(),
});
/**
 * Workflow status schema
 */
export const workflowStatusSchema = z.enum([
    'draft',
    'pending',
    'active',
    'processing',
    'completed',
    'cancelled',
    'failed',
]);
